import React, { useContext, useEffect } from "react";
import Button from "../../../components/Button/button";
import HouseModalContent from "./houseModalContent";
import { HouseCompetitionData } from "./contextProvider";
import { useAppDispatch } from "../../../redux/hooks";
import {
  publishModalClasses,
  publishModalData,
  publishModalProps,
  publishModalVisibility,
} from "../../../redux/features/modal/modalSlice";
import PrizeTicketSelector from "../../../components/PrizeTicketSelector/prizeTicketSelector";
import DOMPurify from "dompurify";
import Skeleton from "react-loading-skeleton";
import { isEmptyPrototype } from "../../../common-utils";
import BotbPassDrawer from "../../botb-pass-drawer/BotbPassDrawer";
import ArrowRightIcon from "../../../shared-components/icons/ArrowRightIcon";

const HousePrizeDetails = () => {
  const houseCompetitionData = useContext(HouseCompetitionData);
  const {
    cashAlternativeText,
    currencySymbol,
    prizeSubtitle,
    prizeTitle,
    discountPercentage,
    isPrizeAvailable,
    prizeId,
    overrideDiscountValue,
    activePriceWithoutCurrency,
    basePriceWithoutCurrency,
    basePriceWithCurrency,
    prizeTags,
    socialReviews,
    shouldShowSubscriptionDrawer,
    hideCashAlternative,
    isHousePrizeOnly,
  } = houseCompetitionData;
  const dispatch = useAppDispatch();

  const openTabContent = (e: React.MouseEvent<HTMLElement>, activeTab: number) => {
    e.stopPropagation();
    dispatch(
      publishModalData({
        modalData: <HouseModalContent activeTab={activeTab} />,
      })
    );
    dispatch(
      publishModalClasses({
        classNames: "rounded-2xl lg:w-[450px] xs:w-[95%] top-[7rem] relative mb-[8rem]",
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  const onEnterNow = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(
      publishModalData({
        modalData: (
          <PrizeTicketSelector
            {...{
              prizeId,
            }}
          />
        ),
      })
    );
    dispatch(
      publishModalClasses({
        classNames: "md:rounded-2xl xs:absolute xs:bottom-0 xs:w-full xs:rounded-b-none xs:rounded-t-2xl z-10",
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  const onSubscriptionTabOpen = () => {
    dispatch(publishModalVisibility({ isOpen: false }));
    dispatch(
      publishModalData({
        modalData: (
          <BotbPassDrawer shouldShowSubscriptionDrawer={shouldShowSubscriptionDrawer} overrideDesktopDisplaying />
        ),
      })
    );
    dispatch(publishModalProps({ withOverlay: false }));
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  const getDiscountPercentValue = () => {
    if (discountPercentage > 0) {
      if (overrideDiscountValue) {
        return overrideDiscountValue;
      }
      return discountPercentage;
    }
  };

  // Adjust footer padding based on button wrapper height
  useEffect(() => {
    const applyPadding = () => {
      const btnWrapper = document.querySelector(".house-prize-btn-wrapper") as HTMLElement | null;
      const footer = document.querySelector(".footer-v2") as HTMLElement | null;
      const subscriptionOverlay = document.querySelector(".subscription-overlay") as HTMLElement | null;

      if (!btnWrapper || !footer) return;

      let height = btnWrapper.offsetHeight;

      if (subscriptionOverlay) {
        height += subscriptionOverlay.offsetHeight;
      }

      const width = window.innerWidth;

      if (width < 1024) {
        footer.style.paddingBottom = `${height}px`;
      } else {
        footer.style.paddingBottom = "0px";
      }
    };

    applyPadding();
    window.addEventListener("resize", applyPadding);
    window.addEventListener("scroll", applyPadding);
    return () => {
      window.removeEventListener("resize", applyPadding);
      window.removeEventListener("scroll", applyPadding);
    };
  }, []);

  const sellingPriceWithoutCurrency = activePriceWithoutCurrency
    ? activePriceWithoutCurrency
    : basePriceWithoutCurrency;
  const showBasePrizeAlongWithSalePrize = sellingPriceWithoutCurrency !== basePriceWithoutCurrency;
  const activePizeClassNames = showBasePrizeAlongWithSalePrize ? "text-[#FF8200]" : "text-[#28282B] pr-4";

  return (
    <div className="flex flex-col gap-2">
      {isEmptyPrototype(houseCompetitionData) ? (
        <Skeleton
          highlightColor="#fff"
          count={1}
          inline
          height={32}
          containerClassName={"lg:static xs:fixed xs:bottom-0 xs:left-0 xs:right-0 xs:z-10 xs:w-full"}
        />
      ) : (
        <>
          <div className="flex gap-2 flex-wrap">
            {prizeTags?.map((url, index) => <img key={index} src={url} alt="prize-tag" className="max-h-[24px]" />)}
          </div>
          <p className="text-[#28282B] font-jost text-[20px] font-medium text-left normal-case">
            {prizeTitle}
            <span className="max-w-[24px] inline-block relative top-[4px] left-[5px]">
              <img
                src="/assets/images/prize-details/tabler-icon-info-circle-filled.svg"
                alt="info-icon"
                className="cursor-pointer"
                onClick={e => {
                  if (hideCashAlternative) {
                    openTabContent(e, 1);
                  } else {
                    openTabContent(e, 2);
                  }
                }}
              />
            </span>
          </p>
        </>
      )}
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(prizeSubtitle) }} />
      {cashAlternativeText && !hideCashAlternative && (
        <div
          className="cursor-pointer border border-solid border-[#E0E0E0] rounded-lg flex p-3 gap-2 mt-4 items-center w-fit"
          onClick={e => openTabContent(e, 1)}>
          <img src="/assets/images/house-competition/pound-sign.png" alt="pound-sign" />

          <span className="uppercase flex gap-2">
            {isHousePrizeOnly ? "Or take" : "Or take up to"}{" "}
            <span
              className="text-[#FF8200]"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(cashAlternativeText),
              }}
            />
            cash
          </span>
          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
            <path
              d="M7.73096 4.5L12.231 9L7.73096 13.5"
              stroke="#898994"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
      <div className="flex items-center mt-2">
        <span className={activePizeClassNames}>
          <span className="text-[0.9375rem] align-top font-bold">{currencySymbol}</span>
          <span className="text-[1.5625rem] font-bold leading-none">{sellingPriceWithoutCurrency}</span>
        </span>
        {showBasePrizeAlongWithSalePrize && (
          <span className="text-[1rem] leading-none px-4 line-through">{basePriceWithCurrency}</span>
        )}
        {discountPercentage > 0 && (
          <span className="text-[1rem] text-[#FF8200]">{`${getDiscountPercentValue()}% off`}</span>
        )}
      </div>
      {isEmptyPrototype(houseCompetitionData) ? (
        <Skeleton
          highlightColor="#fff"
          count={1}
          inline
          height={48}
          containerClassName={"lg:static xs:fixed xs:bottom-0 xs:left-0 xs:right-0 xs:z-10 xs:w-full"}
        />
      ) : (
        <div
          className={`house-prize-btn-wrapper max-lg:fixed xs:bottom-0 xs:left-0 xs:right-0 xs:w-full xs:p-4 lg:p-0 xs:bg-white`}>
          {isPrizeAvailable ? (
            <>
              <Button
                className="w-full uppercase flex justify-center items-center text-white bg-[#FF8200] rounded p-4 lg:mt-4 btnWithArrow"
                onClick={onEnterNow}
                text="">
                <span className="relative leading-[1] flex items-center font-jost">
                  Enter Now
                  <img
                    className="arrowImage absolute left-[104%]"
                    src="/assets/images/chevron.png"
                    alt="Enter-Now-Button"
                    width={12}
                    height={12}
                  />
                </span>
              </Button>
              {shouldShowSubscriptionDrawer ? (
                <button
                  onClick={onSubscriptionTabOpen}
                  className={"w-full mx-0 my-6 p-0 border-0 bg-transparent max-lg:hidden"}>
                  <div className={"flex items-center gap-8 w-full justify-between bg-gray-4 p-3 rounded-lg"}>
                    <div className={"flex items-center gap-3"}>
                      <div>
                        <img
                          src={"/assets/images/botb-pass/bp-btn-desktop.png"}
                          alt={"BOTB Pass"}
                          width={60}
                          height={24}
                          className={"h-6 w-auto"}
                        />
                      </div>
                      <div className={"text-secondary font-jost text-sm font-normal leading-5 text-left"}>
                        Subscribe and save up to 75% with BOTB Pass
                      </div>
                    </div>
                    <ArrowRightIcon className={"size-4 shrink-0 text-[#898994]"} />
                  </div>
                </button>
              ) : null}
            </>
          ) : (
            <Button
              className="w-full uppercase flex justify-center items-center text-[#696971] font-medium text-[17px] bg-white rounded p-4 mt-4 relative border border-solid border-[#696971]"
              onClick={e => {
                e.stopPropagation();
              }}
              text="Not Currently Available"
            />
          )}
        </div>
      )}
      <div
        dangerouslySetInnerHTML={{ __html: socialReviews }}
        className="house-comp-social-reviews mt-4 xs:hidden lg:block"
      />
    </div>
  );
};

export default HousePrizeDetails;
